// src/App.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  AppLayout,
  SideNavigation
} from "@cloudscape-design/components";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation
} from "react-router-dom";
import ProcessosPage    from "./pages/processos/Processos";
import CriarProcessoPage from "./pages/criar-processo/CriarProcesso";
import AtualizarProcessoPage from "./pages/atualizar-processo/AtualizarProcessoPage";
import "@cloudscape-design/global-styles/index.css";

/* ─────────────────────────  ENV  ───────────────────────── */
const stage       = process.env.REACT_APP_STAGE;
const clientId    = "6qmfcp8jsmstb935kn1u2iiugb";
const domain      = "login.conceda.com";
const redirectUri = stage === "prod"
  ? "https://dashboard.conceda.com/"
  : "https://dev.dprtzsmp3o0ut.amplifyapp.com/";
const redirectUriEncoded = encodeURIComponent(redirectUri);

if (stage === "dev") console.log("[DEBUG] Stage is dev.");

const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

/* =========================================================
 * 1. SHELL-LEVEL COMPONENTS
 * =========================================================*/
const SideNav = ({ onLogout, isAdmin }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  return (
    <SideNavigation
      header={{ text: "Menu" }}
      activeHref={location.pathname}
      onFollow={e => {
        e.preventDefault();            // prevent full reload
        if (e.detail.href === "/logout") {
          onLogout();
        } else {
          navigate(e.detail.href);
        }
      }}
      items={[
        { type: "link", text: "Processos",      href: "/processos",      iconName: "folder" },
        ...(isAdmin ? [
          { type: "link", text: "Criar Processo", href: "/criar-processo", iconName: "add-plus" },
          { type: "link", text: "Atualizar Processo", href: "/atualizar-processo", iconName: "add-plus" }
          ] : []),
        { type: "link", text: "Logout", href: "/logout", iconName: "sign-out" }
      ]}
    />
  );
};

/* A thin wrapper so pages can access tokens via `useOutletContext()` */
const Shell = ({ idToken, accessToken, onLogout, isAdmin }) => (
  <AppLayout
    navigation={<SideNav onLogout={onLogout} isAdmin={isAdmin}/>}
    stickyNotifications
    toolsHide
    content={
      <Outlet context={{ idToken, accessToken, onLogout, isAdmin }} />  /* ⚑ */
    }
  />
);

/* =========================================================
 * 2. MAIN APP
 * =========================================================*/
export default function App() {
  const [idToken,     setIdToken]     = useState(localStorage.getItem("id_token"));
  const [accessToken, setAccessToken] = useState(localStorage.getItem("access_token"));
  const [isAdmin, setIsAdmin] = useState(false);

  const getCodeFromUrl = () => new URLSearchParams(window.location.search).get("code");

  useEffect(() => {
    if (idToken) {
      const decodedToken = parseJwt(idToken);
      // Check if user belongs to admin group
      // Cognito typically includes groups in 'cognito:groups' claim
      const userGroups = decodedToken['cognito:groups'] || [];
      setIsAdmin(userGroups.includes('admin-conceda-dashboard'));
    }
  }, [idToken]);

  const exchangeCodeForToken = async code => {
    try {
      const res = await axios.post(
        `https://${domain}/oauth2/token`,
        new URLSearchParams({
          grant_type  : "authorization_code",
          client_id   : clientId,
          code,
          redirect_uri: redirectUri,
        }),
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } },
      );

      const { id_token, access_token } = res.data;
      localStorage.setItem("id_token",     id_token);
      localStorage.setItem("access_token", access_token);
      setIdToken(id_token);
      setAccessToken(access_token);

      // strip ?code=...
      window.history.replaceState({}, document.title, "/processos");
    } catch (err) {
      console.error("Token exchange failed:", err);
    }
  };

  useEffect(() => {
    const code = getCodeFromUrl();

    if (code && (!idToken || !accessToken)) {
      // Just came back from Cognito, exchange the code for a token
      exchangeCodeForToken(code);
    } else if (!code && (!idToken || !accessToken)) {
      // Not authenticated, redirect to login
      const loginUrl = `https://${domain}/login?client_id=${clientId}&redirect_uri=${redirectUriEncoded}&response_type=code&scope=email+openid+phone`;
      window.location.href = loginUrl;
    }
  }, [idToken, accessToken]);

  if (!idToken || !accessToken) return <p>Loading…</p>;

  const handleLogout = () => {
    localStorage.removeItem("id_token");
    localStorage.removeItem("access_token");
    setIdToken(null);
    setAccessToken(null);
    window.location.href = "/";
  };

  return (
    <BrowserRouter>
      <Routes>
        {/* one shell route keeps AppLayout mounted */}
        <Route element={<Shell idToken={idToken} accessToken={accessToken} onLogout={handleLogout} isAdmin={isAdmin}/>}>
          <Route
            path="/processos"
            element={<ProcessosPage />}
          />
          <Route
            path="/criar-processo"
            element={<CriarProcessoPage />}
          />
          <Route
            path="/atualizar-processo"
            element={<AtualizarProcessoPage />}
          />
          <Route path="*" element={<Navigate to="/processos" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
